/**
 * Página de Recuperación de Contraseña
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './AuthPages.css';

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { forgotPassword, confirmForgotPassword } = useAuth();

  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validatePassword = (password) => {
    if (password.length < 8) return t('register.errors.passwordLength');
    if (!/[A-Z]/.test(password)) return t('register.errors.passwordUppercase');
    if (!/[a-z]/.test(password)) return t('register.errors.passwordLowercase');
    if (!/\d/.test(password)) return t('register.errors.passwordNumber');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return t('register.errors.passwordSpecial');
    return null;
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(t('forgotPassword.errors.emailRequired'));
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(email);
      if (result.success) {
        setStep('confirm');
      } else {
        setError(result.error || t('forgotPassword.errors.requestError'));
      }
    } catch (err) {
      setError(t('forgotPassword.errors.requestError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code) {
      setError(t('forgotPassword.errors.codeRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    try {
      const result = await confirmForgotPassword(email, code, newPassword);
      if (result.success) {
        setSuccess(t('forgotPassword.successMessage'));
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(result.error || t('forgotPassword.errors.confirmError'));
      }
    } catch (err) {
      setError(t('forgotPassword.errors.confirmError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Citame</h1>
          <h2>
            {step === 'request' ? t('forgotPassword.title') : t('forgotPassword.confirmTitle')}
          </h2>
        </div>

        {success ? (
          <div className="auth-form">
            <div className="confirmation-message" role="status" aria-live="polite">
              {success}
            </div>
            <Link to="/login" className="btn btn-primary btn-block">
              {t('login.signIn')}
            </Link>
          </div>
        ) : step === 'request' ? (
          <form onSubmit={handleRequestSubmit} className="auth-form" noValidate>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: 'var(--text-base)',
                margin: '0 0 16px 0',
              }}
            >
              {t('forgotPassword.description')}
            </p>

            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="tu@email.com"
                autoComplete="email"
              />
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? t('forgotPassword.sending') : t('forgotPassword.sendCode')}
            </button>

            <Link
              to="/login"
              className="btn btn-ghost btn-block"
              style={{ textAlign: 'center', display: 'block', marginTop: '8px' }}
            >
              {t('common.back')}
            </Link>
          </form>
        ) : (
          <form onSubmit={handleConfirmSubmit} className="auth-form" noValidate>
            <p className="confirmation-message">
              {t('forgotPassword.codeSent')} <strong>{email}</strong>.{' '}
              {t('forgotPassword.enterCode')}
            </p>

            <div className="form-group">
              <label htmlFor="code">{t('forgotPassword.code')}</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError('');
                }}
                placeholder="123456"
                maxLength="6"
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">{t('auth.newPassword')}</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                }}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <small className="form-help">{t('register.passwordHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? t('forgotPassword.resetting') : t('forgotPassword.resetPassword')}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => {
                setStep('request');
                setError('');
              }}
            >
              {t('common.back')}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>
            {t('register.alreadyHaveAccount')} <Link to="/login">{t('register.loginHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
